import { useQuery } from "@tanstack/react-query";
import axiosInstance from "@/axiosConfig";
import { CALENDAR_FISCAL_YEAR } from "@/shared/components/AllocationDatePicker/fiscalYear";
import type { FiscalYearConfig } from "@/shared/components/AllocationDatePicker/fiscalYear";
import { getApiUrl } from "@/shared/utils/api";
import { isForbiddenError } from "@/shared/utils/errorHandling";

type AccountingSettingsApiPayload = {
  closing_day: number | null
  closing_month: number | null
};

/**
 * Clé propre, distincte de celle de la feature accounting : les deux requêtes
 * tapent le même endpoint mais ne stockent pas la même forme (la feature mappe
 * son payload) - partager la clé corromprait l'un des deux caches. La feature
 * invalide cette clé quand la clôture change.
 */
export const FISCAL_YEAR_QUERY_KEY = ["fiscal-year"] as const;

/**
 * ADM-621 - clôture comptable de l'organisation, pour les sélecteurs
 * d'exercice hors comptabilité (subventions, remboursements, bons plans,
 * activités) : dès qu'une clôture est posée, « Exercice 2027 » doit couvrir
 * juillet 2026 - juin 2027 partout, pas seulement en compta.
 *
 * Rend `null` (calendaire) tant que la réponse n'est pas là, si aucune clôture
 * n'est configurée, ou si l'utilisateur n'a pas `service_compta` (l'endpoint
 * répond 403 : la clôture est un réglage compta, mais son absence ne doit pas
 * casser les autres pages).
 */
export const useFiscalYearQuery = (enabled = true): FiscalYearConfig | null => {
  const { data } = useQuery({
    queryKey: FISCAL_YEAR_QUERY_KEY,
    queryFn: async () => {
      const url = getApiUrl("/accounting_settings");
      const response = await axiosInstance.get<{ accounting_settings: AccountingSettingsApiPayload }>(url);
      return response.data.accounting_settings;
    },
    // `select` : référence stable tant que la réponse ne change pas - le
    // picker resynchronise un effet sur l'identité de cet objet.
    select: (settings): FiscalYearConfig | null => {
      if (settings.closing_day === null || settings.closing_month === null) return null;
      if (settings.closing_day === CALENDAR_FISCAL_YEAR.closingDay
        && settings.closing_month === CALENDAR_FISCAL_YEAR.closingMonth) {
        return null;
      }
      return { closingDay: settings.closing_day, closingMonth: settings.closing_month };
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => !isForbiddenError(error) && failureCount < 2,
  });

  return data ?? null;
};
